import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const LOCAL_KEY = 'lorescout_learn_progress';

function readLocal(): Set<string> {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function writeLocal(s: Set<string>) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(Array.from(s)));
  } catch {
    // ignore
  }
}

export function useLearnProgress() {
  const { user } = useAuth();
  const [completed, setCompleted] = useState<Set<string>>(() => readLocal());
  const [loaded, setLoaded] = useState(false);

  // Load from DB when signed in (restored on any device)
  useEffect(() => {
    if (!user) {
      setLoaded(true);
      return;
    }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('learn_progress')
        .select('lesson_key')
        .eq('user_id', user.id);
      if (!active) return;
      if (data) {
        const merged = new Set<string>(readLocal());
        for (const row of data) merged.add(row.lesson_key);
        setCompleted(merged);
        writeLocal(merged);
      }
      setLoaded(true);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const toggle = useCallback(
    async (lessonKey: string) => {
      setCompleted((prev) => {
        const next = new Set(prev);
        const isOn = next.has(lessonKey);
        if (isOn) next.delete(lessonKey);
        else next.add(lessonKey);
        writeLocal(next);

        if (user) {
          if (isOn) {
            supabase
              .from('learn_progress')
              .delete()
              .eq('user_id', user.id)
              .eq('lesson_key', lessonKey)
              .then();
          } else {
            supabase
              .from('learn_progress')
              .upsert(
                { user_id: user.id, lesson_key: lessonKey },
                { onConflict: 'user_id,lesson_key' },
              )
              .then();
          }
        }
        return next;
      });
    },
    [user],
  );

  const isComplete = useCallback((key: string) => completed.has(key), [completed]);

  return { completed, isComplete, toggle, loaded, total: completed.size };
}
